/**
 * Machines — the `/admin/machines/*` endpoints the cabinet is allowed to touch.
 *
 * Owner self-service, and only the self-service half: reading one machine by its
 * number, claiming it, and releasing it again. Extending, blocking and the
 * platform-wide list are `system`-domain and answer 403 here, deliberately — a
 * company that could extend its own licence would not need one.
 *
 * All three send X-Company-Id via requestControl(); the gateway checks the
 * caller's `owner` role in that company's domain and pins the claimed company to
 * the same header, so the body's company_id must match it.
 */
import {requestControl} from '@/shared/api';
import type {MachineCard} from '@/entities/machine/types';

export function fetchMachine(
  machineNumber: string,
  companyId: string,
): Promise<MachineCard> {
  return requestControl<MachineCard>(
    `/admin/machines/${encodeURIComponent(machineNumber)}`,
    companyId,
  );
}

export type ClaimMachineParams = {
  companyId: string;
  /** Optional: a machine already carries a name. Absent leaves it alone. */
  deviceName?: string;
  /** Optional: scope the terminal to a department at onboarding. */
  departmentId?: string;
};

/**
 * Connect a machine to a company.
 *
 * Note what this does *not* do: it grants no licence and extends no term. The
 * terminal arrived licensed and keeps exactly the licence it had. Under the
 * model this replaces, connecting a device *was* how it got licensed — which is
 * why connecting one twice produced two licences, and why a terminal connected
 * again after a factory reset came back already expired.
 */
/**
 * Подключить машину по одноразовому коду.
 *
 * Код — не заявка: он привязан к уже существующей машине и лишь доказывает,
 * что человек стоит рядом с ней. Дальше работает то же самое подключение, что
 * и по номеру, со всеми его отказами.
 */
export function adoptByCode(
  code: string,
  params: {companyId: string; deviceName?: string},
): Promise<MachineCard> {
  return requestControl<MachineCard>('/admin/machines/adopt', params.companyId, {
    method: 'POST',
    body: JSON.stringify({
      code: code.trim().toUpperCase(),
      company_id: params.companyId,
      device_name: params.deviceName ?? '',
    }),
  });
}

export function claimMachine(
  machineNumber: string,
  params: ClaimMachineParams,
): Promise<MachineCard> {
  return requestControl<MachineCard>(
    `/admin/machines/${encodeURIComponent(machineNumber)}/claim`,
    params.companyId,
    {
      method: 'POST',
      body: JSON.stringify({
        company_id: params.companyId,
        device_name: params.deviceName,
        department_id: params.departmentId,
      }),
    },
  );
}

/**
 * Detach a machine from this company — the exact undo of {@link claimMachine}.
 *
 * The licence is untouched: the terminal keeps running, unclaimed, exactly as it did
 * out of the box, and can be claimed again — by this company or another — under the
 * same machine number. There is no terminal wake, because a released machine has no
 * company left to wake it for.
 *
 * This is NOT how a terminal is decommissioned. Releasing leaves the board's own data
 * — faces, personnel, settings — exactly where it is; the factory reset on the device
 * settings screen is what wipes it, and the cloud rows come off with `finalize`. What
 * this fixes is the other half: a board that is finished with in this company but
 * still recorded as belonging to it, which is what makes re-adopting it elsewhere
 * answer "already claimed".
 *
 * Owner self-service, the same way claim is: the header decides the domain, and the
 * gateway checks the caller's role in it. (Casbin asks for `create` here rather than
 * `delete` — derived from the POST — which is a quirk of the method-to-action mapping,
 * not a statement about what the call does.)
 */
export function releaseMachine(
  machineNumber: string,
  companyId: string,
): Promise<MachineCard> {
  return requestControl<MachineCard>(
    `/admin/machines/${encodeURIComponent(machineNumber)}/release`,
    companyId,
    {method: 'POST'},
  );
}
